import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMapMarkerAlt, faCalendar } from '@fortawesome/free-solid-svg-icons'
import api from '@/services/api'
import { useTranslation } from 'react-i18next'
import Spinner from '@/components/Spinner'
import PageHeader from '@/components/PageHeader'
import ErrorDisplay from '@/components/ErrorDisplay'
import PaginationControls from '@/components/PaginationControls'
import SearchAndFilterBar, { type FilterConfig } from '@/components/SearchAndFilterBar'
import { usePagination } from '@/hooks/usePagination'
import { useSearchAndFilter } from '@/hooks/useSearchAndFilter'

/**
 * Venue interface
 */
interface Venue {
  id: string
  organizerId: string
  organizerName: string
  organizerEmail: string
  organizerOrganization: string | null
  name: string
  address: string | null
  city: string
  country: string | null
  googleMapsUrl: string | null
  eventsCount: number
  createdAt: string
  updatedAt: string
}

/**
 * Venues Management Page
 * Dark mode geek aesthetic - Desktop only
 */
export default function VenuesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [venues, setVenues] = useState<Venue[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Search and filter
  const { searchTerm, setSearchTerm, filters, setFilter, filteredData: filteredVenues } = useSearchAndFilter<Venue>({
    data: venues,
    searchFields: ['name', 'organizerName', 'organizerEmail', 'organizerOrganization', 'address', 'city', 'country'],
  })

  // Pagination
  const {
    pagination,
    setPage,
    setPageSize,
    pageCount,
    paginationRange,
    paginatedData,
  } = usePagination(filteredVenues.length)

  // Fetch venues on mount
  useEffect(() => {
    const fetchVenues = async () => {
      try {
        const response = await api.get('/superadmin/venues')
        if (response.data.success && response.data.data?.venues) {
          setVenues(response.data.data.venues)
        }
      } catch (err: any) {
        setError(err.response?.data?.error || t('common.error'))
      } finally {
        setLoading(false)
      }
    }

    fetchVenues()
  }, [t])

  // Get paginated data
  const paginatedVenues = paginatedData(filteredVenues)

  // Handle row click (navigate to details)
  const handleRowClick = (venue: Venue) => {
    navigate(`/venues/${venue.id}`)
  }

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Lieux"
        subtitle="list"
        count={filteredVenues.length}
        subtitleColor="cyan"
        titleGradient="from-cyan-400 to-purple-400"
      />

      <ErrorDisplay error={error} />

      <SearchAndFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Nom, organisateur, ville, adresse..."
        filterValues={filters}
        onFilterChange={setFilter}
      />

      {/* Venues Table */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        {filteredVenues.length === 0 ? (
          <div className="p-8 text-center text-slate-400 font-mono">
            {'$ '} Aucun lieu trouvé
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-800/50 border-b border-slate-700/50">
                  <th className="px-6 py-4 text-left font-mono text-cyan-400 text-xs uppercase tracking-wider cursor-pointer hover:bg-slate-700/50">
                    Nom
                  </th>
                  <th className="px-6 py-4 text-left font-mono text-cyan-400 text-xs uppercase tracking-wider cursor-pointer hover:bg-slate-700/50">
                    Organisateur
                  </th>
                  <th className="px-6 py-4 text-left font-mono text-cyan-400 text-xs uppercase tracking-wider cursor-pointer hover:bg-slate-700/50">
                    Adresse
                  </th>
                  <th className="px-6 py-4 text-left font-mono text-cyan-400 text-xs uppercase tracking-wider cursor-pointer hover:bg-slate-700/50">
                    Ville
                  </th>
                  <th className="px-6 py-4 text-left font-mono text-cyan-400 text-xs uppercase tracking-wider cursor-pointer hover:bg-slate-700/50">
                    Pays
                  </th>
                  <th className="px-6 py-4 text-left font-mono text-cyan-400 text-xs uppercase tracking-wider cursor-pointer hover:bg-slate-700/50">
                    Événements
                  </th>
                  <th className="px-6 py-4 text-left font-mono text-cyan-400 text-xs uppercase tracking-wider cursor-pointer hover:bg-slate-700/50">
                    Créé le
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {paginatedVenues.map((venue) => (
                  <tr
                    key={venue.id}
                    onClick={() => handleRowClick(venue)}
                    className="hover:bg-slate-800/50 transition group cursor-pointer"
                  >
                    <td className="px-6 py-4 text-slate-200 font-semibold">{venue.name}</td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="text-slate-200 font-mono text-sm">{venue.organizerName}</span>
                        <span className="text-slate-400 font-mono text-xs">{venue.organizerEmail}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-300 font-mono text-xs">
                      {venue.address || '—'}
                    </td>
                    <td className="px-6 py-4 text-slate-300 font-mono text-sm">{venue.city}</td>
                    <td className="px-6 py-4 text-slate-300 font-mono text-xs">
                      {venue.country || '—'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon icon={faCalendar} className="text-cyan-400 text-sm" />
                        <span className="text-slate-300 font-mono">{venue.eventsCount}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-300 font-mono text-xs">
                      {new Date(venue.createdAt).toLocaleDateString('fr-FR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <PaginationControls
        pagination={pagination}
        pageCount={pageCount}
        paginationRange={paginationRange}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        itemLabel="lieu(x)"
      />
    </div>
  )
}
